import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, catchError, of, switchMap } from 'rxjs';
import { ButtonModule } from '@openng/optimus-ui/button';
import { CardModule } from '@openng/optimus-ui/card';
import { TableModule } from '@openng/optimus-ui/table';
import { TagModule } from '@openng/optimus-ui/tag';
import {
  CourseRepProgress,
  PracticeCourseAggregateFreshness,
  PracticeCourseDetail,
} from '../../../models/practice-course.types';
import { PracticeCoursesService } from '../../../services/practice-courses.service';

@Component({
  selector: 'app-practice-course-detail',
  imports: [CommonModule, RouterModule, ButtonModule, CardModule, TableModule, TagModule],
  templateUrl: './practice-course-detail.component.html',
  styleUrl: '../practice-courses.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PracticeCourseDetailComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly service = inject(PracticeCoursesService);
  private readonly destroyRef = inject(DestroyRef);

  protected detail = signal<PracticeCourseDetail | null>(null);
  protected reps = signal<CourseRepProgress[]>([]);
  protected total = signal(0);
  protected loading = signal(true);
  protected repLoading = signal(false);
  protected error = signal<string | null>(null);
  /** Freshness of the whole course aggregate (course-wide scope). */
  protected courseFreshness = signal<PracticeCourseAggregateFreshness | null>(null);
  /** Freshness of the currently-loaded rep page (page scope, replaced on every page load). */
  protected pageFreshness = signal<PracticeCourseAggregateFreshness | null>(null);
  protected readonly staleEnrollmentCount = computed(() =>
    this.courseFreshness()?.status === 'stale' ? this.courseFreshness()!.staleEnrollmentCount : 0,
  );
  protected readonly courseVersionId = this.route.snapshot.paramMap.get('courseVersionId')!;

  /** One request stream for rep paging: a newer page cancels the in-flight request instead of
   * racing it, so a stale page can never overwrite the newer one. */
  private readonly repRequest$ = new Subject<
    { first?: number | null; rows?: number | null } | undefined
  >();

  /** One request stream for the course metadata, for the same reason as the rep stream: `load()` is
   * bound to both "Try again" and "Refresh", so repeated clicks on a slow connection would otherwise
   * race and let an older response overwrite a newer one. */
  private readonly courseRequest$ = new Subject<void>();

  /** The last paginator event, replayed when the user retries. `p-table` fires `(onLazyLoad)` once on
   * init, so this stays undefined until the table has asked for its first page — without that guard a
   * retry before first load would fire a second, redundant page request. */
  private lastRepEvent: { first?: number | null; rows?: number | null } | undefined;
  private repsRequested = false;

  constructor() {
    // Both subscriptions live here, not in load(). load() is a click handler; subscribing inside it
    // added a *new* subscriber to each Subject on every "Try again"/"Refresh", so after n clicks a
    // single page change fanned out into n identical HTTP requests.
    this.courseRequest$
      .pipe(
        switchMap(() =>
          this.service.getCourse(this.courseVersionId).pipe(
            catchError((error) => {
              this.error.set(error.message);
              this.loading.set(false);
              return of(null);
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (detail) => {
          if (!detail) return;
          this.detail.set(detail);
          this.courseFreshness.set(detail.freshness ?? null);
          this.loading.set(false);
        },
      });
    this.repRequest$
      .pipe(
        // catchError inside the switchMap: a root-subscriber error would permanently kill the
        // stream, breaking every later page change and the "Try again" affordance.
        switchMap((event) =>
          this.fetchReps(event).pipe(
            catchError((error) => {
              this.repLoading.set(false);
              this.error.set(error.message);
              return of(null);
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => {
          if (!response) return;
          this.reps.set(response.data);
          this.total.set(response.total);
          // Direct replacement, not a merge: a clean page clears the page banner, and a stale page
          // can never carry over into a fresh one.
          this.pageFreshness.set(response.freshness ?? null);
          this.repLoading.set(false);
        },
      });
    this.load();
  }

  /** "Try again" and "Refresh" both land here. Both requests write to the same `error` signal, so a
   * failed reps fetch shows the page-level error banner — retrying only the course metadata would
   * clear the banner while leaving the rep table empty. */
  protected load() {
    this.loading.set(true);
    this.error.set(null);
    this.courseRequest$.next();
    if (this.repsRequested) this.repRequest$.next(this.lastRepEvent);
  }

  private fetchReps(event?: { first?: number | null; rows?: number | null }) {
    const rows = event?.rows ?? 20;
    const page = Math.floor((event?.first ?? 0) / rows);
    this.repLoading.set(true);
    this.error.set(null);
    return this.service.getProgress(this.courseVersionId, { page, pageSize: rows });
  }

  protected loadReps(event?: { first?: number | null; rows?: number | null }) {
    this.lastRepEvent = event;
    this.repsRequested = true;
    this.repRequest$.next(event);
  }

  protected openRep(rep: CourseRepProgress) {
    this.router.navigate(['/practice-courses', this.courseVersionId, 'reps', rep.user.id]);
  }

  protected percent(value: number | null | undefined) {
    return Math.round((value ?? 0) * 100);
  }

  protected severity(status: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (status === 'CERTIFIED' || status === 'READY_FOR_LIVE_CALLS' || status === 'ACTIVE')
      return 'success';
    if (status === 'BLOCKED') return 'danger';
    if (status === 'NEEDS_MANAGER_REVIEW') return 'warn';
    if (status === 'ARCHIVED' || status === 'CANCELED') return 'secondary';
    return 'info';
  }

  protected label(value: string) {
    return value
      .replaceAll('_', ' ')
      .toLowerCase()
      .replace(/^./, (char) => char.toUpperCase());
  }

  protected totalCount(counts: Record<string, number> | null | undefined) {
    if (!counts) return 0;
    return Object.values(counts).reduce((total, count) => total + count, 0);
  }

  protected assignmentSources(counts: Record<string, number> | null | undefined) {
    if (!counts) return '';
    const parts: string[] = [];
    if (counts['TEAM']) parts.push(`Team: ${counts['TEAM']}`);
    if (counts['USER']) parts.push(`Individual: ${counts['USER']}`);
    return parts.join(' · ');
  }
}
